import { useState } from "react";
import { useLang } from "../i18n/LanguageContext";

export default function SharePanel() {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const url =
    typeof window !== "undefined" ? window.location.origin : "";
  const message = t("share_message") + url;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older/insecure contexts
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent(
    "Soccer Troop · Registro"
  )}&body=${encodeURIComponent(message)}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&color=080808&bgcolor=ffffff&margin=8&data=${encodeURIComponent(
    url
  )}`;

  return (
    <div
      data-testid="share-panel"
      className="rounded-2xl bg-gradient-to-br from-[#161616] to-[#1a0e16] border border-[#e8559a]/30 p-5 sm:p-6 mb-6"
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-shrink-0 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#e8559a]/15 border border-[#e8559a]/40 flex items-center justify-center text-2xl">
            🔗
          </div>
          <div className="lg:hidden">
            <h3 className="font-bebas text-xl tracking-widest text-white leading-tight">
              {t("share_title")}
            </h3>
            <p className="text-xs text-neutral-400">{t("share_subtitle")}</p>
          </div>
        </div>

        <div className="hidden lg:block flex-shrink-0 min-w-0">
          <h3 className="font-bebas text-xl tracking-widest text-white leading-tight">
            {t("share_title")}
          </h3>
          <p className="text-xs text-neutral-400">{t("share_subtitle")}</p>
        </div>

        <div className="flex-1 flex items-stretch gap-2 min-w-0">
          <div
            className="flex-1 min-w-0 bg-[#0a0a0a] border border-[#252525] rounded-lg px-3 py-2 flex items-center"
            data-testid="share-url"
          >
            <span className="text-[#e8559a] text-xs sm:text-sm font-mono truncate select-all">
              {url}
            </span>
          </div>
          <button
            onClick={copy}
            data-testid="copy-link-btn"
            className={`flex-shrink-0 px-4 py-2 rounded-lg font-bebas text-sm tracking-widest transition-all ${
              copied
                ? "bg-emerald-500 text-white"
                : "bg-[#e8559a] hover:bg-[#c9407f] text-white"
            }`}
          >
            {copied ? `✓ ${t("copied")}` : `⧉ ${t("copy_link")}`}
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-whatsapp-btn"
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0f0f0f] border border-[#252525] hover:border-[#25d366]/60 hover:bg-[#25d366]/5 text-sm text-neutral-200 transition-all"
        >
          <span className="text-base">💬</span> {t("share_whatsapp")}
        </a>
        <a
          href={emailHref}
          data-testid="share-email-btn"
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0f0f0f] border border-[#252525] hover:border-[#e8559a]/50 hover:bg-[#e8559a]/5 text-sm text-neutral-200 transition-all"
        >
          <span className="text-base">✉</span> {t("share_email")}
        </a>
        <button
          type="button"
          onClick={() => setShowQR((v) => !v)}
          data-testid="share-qr-btn"
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0f0f0f] border border-[#252525] hover:border-[#e8559a]/50 hover:bg-[#e8559a]/5 text-sm text-neutral-200 transition-all"
        >
          <span className="text-base">▦</span>
          {showQR ? t("share_qr_close") : t("share_qr")}
        </button>
      </div>

      {showQR && (
        <div
          className="mt-4 flex flex-col items-center gap-3 p-4 rounded-xl bg-[#0a0a0a] border border-[#252525]"
          data-testid="share-qr-block"
        >
          <img
            src={qrSrc}
            alt="QR code"
            width={240}
            height={240}
            className="rounded-lg bg-white p-1"
          />
          <p className="text-xs text-neutral-500 text-center max-w-xs">
            Escanea con la cámara para abrir el formulario · Scan with your camera to open the form
          </p>
        </div>
      )}
    </div>
  );
}
